import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import SignInModal from '../components/SignInModal';

export interface SignInModalOptions {
  returnUrl?: string;
  afterSignInUrl?: string;
  afterSignUpUrl?: string;
}

interface SignInModalContextValue {
  isOpen: boolean;
  isSignedIn: boolean;
  isAuthLoaded: boolean;
  openSignInModal: (options?: SignInModalOptions) => void;
  closeSignInModal: () => void;
  requireAuth: (callback: () => void, options?: SignInModalOptions) => boolean;
}

const SignInModalContext = createContext<SignInModalContextValue | null>(null);

interface SignInModalProviderProps {
  children: ReactNode;
}

export const SignInModalProvider: React.FC<SignInModalProviderProps> = ({ children }) => {
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState<SignInModalOptions>({});

  const openSignInModal = useCallback((options?: SignInModalOptions) => {
    setModalOptions(options || {});
    setIsOpen(true);
  }, []);

  const closeSignInModal = useCallback(() => {
    setIsOpen(false);
    setModalOptions({});
  }, []);

  const requireAuth = useCallback(
    (callback: () => void, options?: SignInModalOptions): boolean => {
      if (!isAuthLoaded) {
        console.warn('[SignInModalContext] Auth not loaded yet');
        return false;
      }

      if (isSignedIn) {
        callback();
        return true;
      } else {
        openSignInModal(options);
        return false;
      }
    },
    [isAuthLoaded, isSignedIn, openSignInModal]
  );

  const value: SignInModalContextValue = {
    isOpen,
    isSignedIn: !!isSignedIn,
    isAuthLoaded,
    openSignInModal,
    closeSignInModal,
    requireAuth,
  };

  return (
    <SignInModalContext.Provider value={value}>
      {children}
      <SignInModal
        isOpen={isOpen}
        onClose={closeSignInModal}
        returnUrl={modalOptions.returnUrl}
        afterSignInUrl={modalOptions.afterSignInUrl}
        afterSignUpUrl={modalOptions.afterSignUpUrl}
      />
    </SignInModalContext.Provider>
  );
};

export function useSignInModalContext(): SignInModalContextValue {
  const context = useContext(SignInModalContext);
  if (!context) {
    throw new Error('useSignInModalContext must be used within a SignInModalProvider');
  }
  return context;
}
